import { initI18n } from './init';
import { collectRegionalBundles } from './regional-bundles';
import { attachTopics, type TopicSources } from './topics';

type Bundle = Record<string, Record<string, unknown>>;

/**
 * Translation bundles shipped by a frontend package (e.g. `@tale/ui`).
 * Mirrors the shape the host service uses for its
 * own translations: per-locale namespace trees plus an optional
 * cross-locale `global` for keys that read the same in every language
 * (e.g. `languageSwitcher.locales.*` — the language names display in
 * their own native form).
 */
export interface PackageMessages {
  /**
   * One entry per locale (`en`, `de`, `fr`, plus optional `xx-YY`
   * regional codes). Each entry is a namespace → keys tree.
   */
  bundles: Record<string, Bundle>;
  /**
   * Cross-locale keys merged into every base locale. The
   * convention here matches `services/<x>/messages/global.json`.
   */
  global?: Bundle;
}

interface InitServiceParams {
  /**
   * Base-locale bundles loaded statically by the service: `en` always (empty
   * when its messages come per topic, `topics`), and `de`/`fr` unless they
   * are in `lazyBundles` or come per topic.
   */
  bundles: {
    en: Bundle;
    de?: Bundle;
    fr?: Bundle;
  } & Record<string, Bundle | undefined>;
  /**
   * Base locales the service fetches when a session first needs them, each
   * answering the service's own bundle for it (`() => import('…/de.yml')`);
   * the package bundles merge in as they do for the static ones. English is
   * every locale's fallback, so it is never one of them.
   */
  lazyBundles?: Partial<Record<'de' | 'fr', () => Promise<Bundle>>>;
  /**
   * The service's messages per topic (`messages/<locale>/<topic>.yml`):
   * English registered by the modules that read it (the `messageTopics` vite
   * plugin), every other locale fetched per topic as the session needs it.
   * The package bundles are in the store from the start, in every locale.
   */
  topics?: TopicSources;
  /**
   * Result of `import.meta.glob('…/messages/*-*.json', { eager: true, import: 'default' })`.
   * Vite requires the glob pattern to be a literal at the call site, so each
   * service evaluates it locally and passes the result here.
   */
  regional: Record<string, Bundle>;
  /** Cross-namespace global keys (typically `services/<x>/messages/global.json`). */
  global?: Bundle;
  /**
   * Per-package bundles to merge in *before* the service's own bundles, so
   * any service-level key wins on conflict. Each entry is what a package
   * exports from `@tale/<name>/i18n/messages`. A package may ship keys in
   * the shared namespaces its components speak (`common.actions.*`,
   * `common.aria.*`): the merge is deep, so a service that redeclares one
   * key overrides that key alone, never the sibling keys around it.
   */
  packages?: ReadonlyArray<PackageMessages>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...target };
  for (const [key, value] of Object.entries(source)) {
    const existing = out[key];
    out[key] =
      isPlainObject(existing) && isPlainObject(value)
        ? deepMerge(existing, value)
        : value;
  }
  return out;
}

/**
 * Deep merge of message trees — later entries win per key. A namespace
 * present in several bundles keeps every key each of them defines; only a
 * key defined more than once is overridden, by the last bundle.
 */
export function mergeBundles(
  ...bundles: ReadonlyArray<Bundle | undefined>
): Bundle {
  let out: Record<string, unknown> = {};
  for (const b of bundles) {
    if (!b) continue;
    out = deepMerge(out, b);
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- every top-level value is a namespace tree; deepMerge never flattens one
  return out as Bundle;
}

function packageBundle(
  packages: ReadonlyArray<PackageMessages>,
  locale: string,
): Bundle | undefined {
  return mergeBundles(...packages.map((p) => p.bundles[locale]));
}

/**
 * Thin wrapper over `initI18n` + `collectRegionalBundles` that captures the
 * `services/<x>/lib/i18n/i18n.ts` boilerplate: load base bundles statically,
 * `import.meta.glob` the regional variants, layer in any package bundles,
 * and initialise. Every service does the same thing — this is the one place
 * it lives.
 */
export function initServiceI18n({
  bundles,
  lazyBundles = {},
  regional,
  global,
  packages = [],
  topics,
}: InitServiceParams) {
  const regionalBundles = collectRegionalBundles(regional);

  const merged: {
    en: Bundle;
    de?: Bundle;
    fr?: Bundle;
  } & Record<string, Bundle | undefined> = {
    en: mergeBundles(packageBundle(packages, 'en'), bundles.en),
  };
  const lazy: Partial<Record<'de' | 'fr', () => Promise<Bundle>>> = {};
  for (const base of ['de', 'fr'] as const) {
    const fetch = lazyBundles[base];
    const bundle = bundles[base];
    if (fetch !== undefined) {
      lazy[base] = () =>
        fetch().then((own) => mergeBundles(packageBundle(packages, base), own));
    } else if (bundle !== undefined || topics !== undefined) {
      merged[base] = mergeBundles(packageBundle(packages, base), bundle);
    }
  }

  // Layer remaining locales (regional overrides shipped by the service,
  // extra base-locales the service registered, or regional bundles a
  // package wants to ship even when the service itself has no override).
  const extras = new Set<string>([
    ...Object.keys(bundles),
    ...Object.keys(regionalBundles),
    ...packages.flatMap((p) => Object.keys(p.bundles)),
  ]);
  extras.delete('en');
  extras.delete('de');
  extras.delete('fr');
  for (const locale of extras) {
    const fromService = bundles[locale] ?? regionalBundles[locale];
    merged[locale] = mergeBundles(packageBundle(packages, locale), fromService);
  }

  // Package globals fold in before the service's own global so any key
  // the service redeclares overrides whatever a package shipped.
  const mergedGlobal = mergeBundles(...packages.map((p) => p.global), global);

  const i18n = initI18n({
    bundles: merged,
    global: mergedGlobal,
    lazy,
  });
  if (topics !== undefined) attachTopics(i18n, topics);
  return i18n;
}
